<template>
  <UiCard v-if="attributes.length > 0" class="catalog-attribute-filters">
    <div class="catalog-attribute-filters__header">
      <h3>Характеристики</h3>
      <button
        v-if="hasSelections"
        type="button"
        class="catalog-attribute-filters__link"
        @click="emit('clear')"
      >
        Сбросить
      </button>
    </div>

    <div class="catalog-attribute-filters__grid">
      <div
        v-for="attribute in attributes"
        :key="attribute.id"
        class="catalog-attribute-filters__item"
      >
        <div class="catalog-attribute-filters__label">
          {{ attribute.name }}
          <span v-if="attribute.unit" class="catalog-attribute-filters__muted">
            , {{ attribute.unit }}
          </span>
        </div>

        <div v-if="attribute.type === 'number'" class="catalog-attribute-filters__range">
          <div>
            <label class="catalog-attribute-filters__label">От</label>
            <UiInput
              :model-value="rangeFilters[String(attribute.id)]?.min ?? ''"
              input-filter="decimal"
              @update:modelValue="(value) => emit('update:range-min', attribute.id, value)"
            />
          </div>
          <div>
            <label class="catalog-attribute-filters__label">До</label>
            <UiInput
              :model-value="rangeFilters[String(attribute.id)]?.max ?? ''"
              input-filter="decimal"
              @update:modelValue="(value) => emit('update:range-max', attribute.id, value)"
            />
          </div>
        </div>

        <UiSelect
          v-else-if="attribute.type === 'boolean'"
          :model-value="booleanFilters[String(attribute.id)] ?? ''"
          :options="BOOLEAN_FILTER_OPTIONS"
          @update:modelValue="(value) => emit('update:boolean', attribute.id, value)"
        />

        <div
          v-else-if="attributeOptionLabels(attribute).length > 0"
          class="catalog-attribute-filters__checkbox-grid"
        >
          <label
            v-for="option in attributeOptionLabels(attribute)"
            :key="`${attribute.id}-${option}`"
            class="catalog-attribute-filters__checkbox"
          >
            <input
              type="checkbox"
              :value="option"
              :checked="Boolean(valueFilters[String(attribute.id)]?.includes(option))"
              @change="emit('toggle-option', attribute.id, option)"
            />
            <span class="catalog-attribute-filters__checkbox-box" aria-hidden="true" />
            <span class="catalog-attribute-filters__checkbox-label">{{ option }}</span>
          </label>
        </div>
      </div>
    </div>
  </UiCard>
</template>

<script setup lang="ts">
import UiCard from '@/shared/ui/UiCard.vue'
import UiInput from '@/shared/ui/UiInput.vue'
import UiSelect from '@/shared/ui/UiSelect.vue'
import {
  attributeOptionLabels,
  type AttributeBooleanFilters,
  type AttributeRangeFilters,
  type AttributeValueFilters,
  type DynamicAttributeFilter,
} from '@/modules/catalog/model/catalogFilters'

defineProps<{
  attributes: DynamicAttributeFilter[]
  valueFilters: AttributeValueFilters
  rangeFilters: AttributeRangeFilters
  booleanFilters: AttributeBooleanFilters
  hasSelections: boolean
}>()

const emit = defineEmits<{
  clear: []
  'update:range-min': [attributeId: number, value: string | number]
  'update:range-max': [attributeId: number, value: string | number]
  'update:boolean': [attributeId: number, value: string]
  'toggle-option': [attributeId: number, option: string]
}>()

const BOOLEAN_FILTER_OPTIONS = [
  { label: 'Все', value: '' },
  { label: 'Да', value: 'true' },
  { label: 'Нет', value: 'false' },
]
</script>

<style scoped>
.catalog-attribute-filters {
  margin-bottom: 16px;
}

.catalog-attribute-filters__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.catalog-attribute-filters__header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.catalog-attribute-filters__link {
  background: none;
  border: none;
  padding: 0;
  color: var(--color-accent);
  cursor: pointer;
  text-decoration: underline;
  font-size: 14px;
}

.catalog-attribute-filters__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
}

.catalog-attribute-filters__item {
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 8px;
  padding: 12px;
}

.catalog-attribute-filters__label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-text-muted, #6b7280);
  margin-bottom: 6px;
}

.catalog-attribute-filters__muted {
  font-weight: 400;
  text-transform: none;
  letter-spacing: normal;
}

.catalog-attribute-filters__range {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.catalog-attribute-filters__checkbox-grid {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* Нативный input скрыт, состояние рисует span */
.catalog-attribute-filters__checkbox {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #334155;
  cursor: pointer;
  user-select: none;
}

.catalog-attribute-filters__checkbox input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.catalog-attribute-filters__checkbox-box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
  background: #fff;
  border: 1.5px solid #cbd5e1;
  border-radius: 5px;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;
}

.catalog-attribute-filters__checkbox input:checked + .catalog-attribute-filters__checkbox-box {
  background: var(--color-accent);
  border-color: var(--color-accent);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
}

.catalog-attribute-filters__checkbox input:focus-visible + .catalog-attribute-filters__checkbox-box {
  box-shadow: 0 0 0 3px rgba(20, 51, 94, 0.15);
}
</style>
